/**
 * Reading the tronc commun référentiels from the lesson form.
 *
 * The dataset in `src/data/referentiel-p3/` is generated from the nine official
 * PDFs by `scripts/referentiel/extract.mjs`; this module is the only place the
 * rest of the app touches it.
 *
 * Only P3 is extracted so far. Everything here answers "nothing for this
 * subject and year" rather than throwing, so the form degrades to an empty
 * state on any other year instead of breaking.
 */
import { ATTENDUS_BY_SUBJECT, type Attendu } from '../data/referentiel-p3/index.ts';
import type { Level } from './levels.ts';

export type { Attendu };

/** The years the dataset covers today. */
export const LEVELS_WITH_REFERENTIEL: readonly Level[] = ['P3'];

const BY_ID = new Map<string, Attendu>();
for (const list of Object.values(ATTENDUS_BY_SUBJECT)) {
  for (const attendu of list) BY_ID.set(attendu.id, attendu);
}

export function hasReferentiel(level: Level): boolean {
  return LEVELS_WITH_REFERENTIEL.includes(level);
}

/** The attendus a lesson in this subject and year can be tagged with. */
export function attendusFor(subject: string, level: Level): readonly Attendu[] {
  if (!hasReferentiel(level)) return [];
  return ATTENDUS_BY_SUBJECT[subject] ?? [];
}

/**
 * Look up a saved id.
 *
 * Returns undefined for an attendu that no longer exists — a lesson saved
 * before a dataset update keeps its id, and the form shows it as unknown rather
 * than dropping it silently.
 */
export function attenduById(id: string): Attendu | undefined {
  return BY_ID.get(id);
}

/** The attendus behind a lesson's saved ids, in the order they were chosen. */
export function attendusByIds(ids: readonly string[]): readonly Attendu[] {
  const out: Attendu[] = [];
  for (const id of ids) {
    const attendu = BY_ID.get(id);
    if (attendu) out.push(attendu);
  }
  return out;
}

/** Enough of an attendu to recognise it on a lesson card. */
export function attenduShortLabel(attendu: Attendu, max = 48): string {
  const text = attendu.text.replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 20 ? lastSpace : max).trimEnd()}…`;
}

export interface AttenduGroup {
  section: string;
  parent: string;
  attendus: readonly Attendu[];
}

/**
 * Attendus grouped for display: section, then the contenu d'apprentissage they
 * hang from. Both can be empty when the référentiel does not name them, and the
 * grouping keeps the source order rather than sorting.
 */
export function groupAttendus(attendus: readonly Attendu[]): readonly AttenduGroup[] {
  const groups: AttenduGroup[] = [];
  for (const attendu of attendus) {
    const last = groups[groups.length - 1];
    if (last && last.section === attendu.section && last.parent === attendu.parent) {
      (last.attendus as Attendu[]).push(attendu);
      continue;
    }
    groups.push({ section: attendu.section, parent: attendu.parent, attendus: [attendu] });
  }
  return groups;
}

/** Case- and accent-insensitive match, so "geometrie" finds "géométrie". */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function searchAttendus(attendus: readonly Attendu[], query: string): readonly Attendu[] {
  const needle = fold(query.trim());
  if (needle === '') return attendus;
  const words = needle.split(/\s+/);
  return attendus.filter((attendu) => {
    const haystack = fold(`${attendu.text} ${attendu.parent} ${attendu.section} ${attendu.champ}`);
    return words.every((word) => haystack.includes(word));
  });
}
